package com.evoapps.evoshift.ui

import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.ColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Typography
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.OnScreen
import androidx.compose.ui.text.font.FontWeight
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.open_sans
import org.jetbrains.compose.resources.Font

// The app's colours (SH-196): the base's and the accent's of the appearance on show (AppAppearance).
/** The base colour (navy in the default look). */
val DeepNavy: Color get() = AppAppearance.palette.base.base
// Lighter, more subtle cream background to avoid being "too yellow"
val Cream = Color(0xFFFDFBF7)
/** The vivid accent (gold in the default look). */
val Gold: Color get() = AppAppearance.palette.accent.vivid
// A subtle accent tint for regular cards to differentiate them
val GoldTintedWhite: Color get() = AppAppearance.palette.accent.tint
val DarkText = Color(0xFF1A1A1A)
val LightText = Color(0xFFF0F0F0)
val White = Color(0xFFFFFFFF)

/*
 * SH-230: the light cards. A clean white surface with a hairline edge (and a soft shadow) on the
 * cream page; before, a cream fill (the accent's tint, a cream gradient) that blended into the
 * page and looked washed out.
 */
/** The light cards' surface. */
val CardSurface = White
/** The light cards' hairline edge: the base at a low alpha, so it follows the appearance. */
val CardHairline: Color get() = AppAppearance.palette.base.base.copy(alpha = 0.14f)
/** A neutral light fill for strips and boxes inside the screens (Material's surfaceVariant). */
val NeutralSurface = Color(0xFFF2F4F7)
/** A "still to do" strip (WarningNote): amber, with the screens' warning text on it. */
val WarningSurface = Color(0xFFFFF3E0)
/** The amber edge of a card that needs attention. */
val WarningEdge = Color(0xFFE0A13A)

private fun lightScheme(p: ThemePalette) = lightColorScheme(
    primary = p.base.base,
    onPrimary = Cream,
    primaryContainer = p.base.base,
    onPrimaryContainer = LightText,
    secondary = p.accent.vivid,
    onSecondary = p.base.base,
    secondaryContainer = p.accent.vivid, // Useful for accents
    onSecondaryContainer = p.base.base,
    tertiary = p.accent.vivid,
    onTertiary = p.base.base,
    background = Cream,
    onBackground = p.base.base,
    surface = Cream,
    onSurface = p.base.base,
    // SH-230: neutral, not the accent's cream tint (cards are white, see CardSurface).
    surfaceVariant = NeutralSurface,
    surfaceContainerLowest = CardSurface,
    onSurfaceVariant = p.base.base,
    error = Color(0xFFBA1A1A),
    onError = White
)

private val LightSchemes = PerAppearance(::lightScheme)

/** The cream screens' colour scheme of the appearance on show. */
internal val AppColorScheme: ColorScheme get() = LightSchemes.current

/** The cream screens' colour scheme of [appearance] (tests, previews). */
fun appColorScheme(appearance: Appearance): ColorScheme = LightSchemes.of(appearance)

/**
 * Bundled font (Open Sans, OFL): the web target renders on a canvas without system fonts, so
 * every text style must name a font that ships with the app. Variable font: one file, all weights.
 */
@Composable
private fun appFontFamily(): FontFamily = FontFamily(
    Font(Res.font.open_sans, FontWeight.Normal),
    Font(Res.font.open_sans, FontWeight.Medium),
    Font(Res.font.open_sans, FontWeight.SemiBold),
    Font(Res.font.open_sans, FontWeight.Bold)
)

/** Material defaults, every style on the bundled font. */
private fun Typography.withFontFamily(family: FontFamily) = Typography(
    displayLarge = displayLarge.copy(fontFamily = family),
    displayMedium = displayMedium.copy(fontFamily = family),
    displaySmall = displaySmall.copy(fontFamily = family),
    headlineLarge = headlineLarge.copy(fontFamily = family),
    headlineMedium = headlineMedium.copy(fontFamily = family),
    headlineSmall = headlineSmall.copy(fontFamily = family),
    titleLarge = titleLarge.copy(fontFamily = family),
    titleMedium = titleMedium.copy(fontFamily = family),
    titleSmall = titleSmall.copy(fontFamily = family),
    bodyLarge = bodyLarge.copy(fontFamily = family),
    bodyMedium = bodyMedium.copy(fontFamily = family),
    bodySmall = bodySmall.copy(fontFamily = family),
    labelLarge = labelLarge.copy(fontFamily = family),
    labelMedium = labelMedium.copy(fontFamily = family),
    labelSmall = labelSmall.copy(fontFamily = family)
)

private val AppTextSelectionColors = PerAppearance { p ->
    TextSelectionColors(handleColor = p.accent.metallic, backgroundColor = p.accent.metallic.copy(alpha = 0.4f))
}

@Composable
fun AppTheme(
    content: @Composable () -> Unit
) {
    MaterialTheme(
        colorScheme = AppColorScheme,
        typography = Typography().withFontFamily(appFontFamily())
    ) {
        // Material derives the selection handles ("teardrop" dots) from the navy primary: on
        // the navy screens they vanished. Gold reads on navy and on cream alike.
        CompositionLocalProvider(
            LocalTextSelectionColors provides AppTextSelectionColors.current,
            // SH-196: the cream screens' roles of the appearance on show.
            LocalOnColours provides OnScreen,
            content = content
        )
    }
}
